const toFiniteNumber = (...values: unknown[]): number => {
	for (const value of values) {
		if (typeof value === "number" && Number.isFinite(value)) return value;
		if (typeof value === "string") {
			const trimmed = value.trim();
			if (!trimmed) continue;
			const parsed = Number(trimmed);
			if (Number.isFinite(parsed)) return parsed;
		}
	}
	return 0;
};

const getRecord = (value: unknown): Record<string, unknown> | null => {
	if (!value || typeof value !== "object") return null;
	return value as Record<string, unknown>;
};

const pickFirstMetric = (provider: unknown): Record<string, unknown> | null => {
	const root = getRecord(provider);
	if (!root) return null;

	const candidates = [
		root.portfolio_metrics,
		root.portfolioMetrics,
		root.metrics,
		root.portfolio_metric,
		root.portfolioMetric,
	];

	for (const candidate of candidates) {
		if (Array.isArray(candidate) && candidate.length > 0) {
			const first = getRecord(candidate[0]);
			if (first) return first;
		}
		const objectCandidate = getRecord(candidate);
		if (objectCandidate) return objectCandidate;
	}

	return null;
};

export type ProviderResolvedStats = {
	totalOrders: number;
	completedOrders: number;
	responseRate: number;
	responseTime: number;
};

export const resolveProviderReviewsCount = (provider: unknown): number => {
	const root = getRecord(provider);
	if (!root) return 0;

	const users = getRecord(root.users);
	const listCandidates = [users?.reviews_qualified, users?.reviewsQualified];
	for (const candidate of listCandidates) {
		if (Array.isArray(candidate)) return candidate.length;
	}

	const metric = pickFirstMetric(root);
	return Math.max(
		0,
		Math.trunc(
			toFiniteNumber(
				root.reviews_count,
				root.review_count,
				root.reviewsCount,
				root.total_reviews,
				root.totalReviews,
				users?.reviews_count,
				users?.review_count,
				users?.reviewsCount,
				metric?.reviews_count,
				metric?.review_count,
				metric?.total_reviews,
				metric?.totalReviews,
			),
		),
	);
};

export const resolveProviderStats = (provider: unknown): ProviderResolvedStats => {
	const root = getRecord(provider);
	const metric = pickFirstMetric(provider);

	const totalOrders = Math.max(
		0,
		Math.trunc(
			toFiniteNumber(
				metric?.total_orders,
				metric?.totalOrders,
				metric?.orders_total,
				root?.total_orders,
				root?.totalOrders,
				root?.orders_total,
			),
		),
	);

	const completedOrders = Math.max(
		0,
		Math.trunc(
			toFiniteNumber(
				metric?.orders_completed,
				metric?.completed_orders,
				metric?.ordersCompleted,
				metric?.completedOrders,
				metric?.work_completed,
				metric?.jobs_completed,
				root?.orders_completed,
				root?.completed_orders,
				root?.ordersCompleted,
				root?.completedOrders,
				root?.work_completed,
				root?.jobs_completed,
			),
		),
	);

	const responseRate = Math.max(
		0,
		Math.trunc(
			toFiniteNumber(
				metric?.response_rate,
				metric?.responseRate,
				root?.response_rate,
				root?.responseRate,
			),
		),
	);

	const responseTime = Math.max(
		0,
		Math.trunc(
			toFiniteNumber(
				metric?.response_time,
				metric?.responseTime,
				root?.response_time,
				root?.responseTime,
			),
		),
	);

	return {
		totalOrders,
		completedOrders,
		responseRate,
		responseTime,
	};
};
